import React, { useCallback, useEffect, useMemo } from 'react';
import { PreferenceEnum, PreferenceEnumType, PreferencesResource } from '@mark43/cad-api';
import { BlankModal, Divider, HStack, ModalBody, ModalFooter, ModalHeader } from '@cad-arc';
import { useLocation } from 'react-router-dom';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { useUserActionTracker } from '../monitoring/user-action-tracker/use-user-action-tracker';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useRefreshEnabledFeatures } from '../common/hooks/enabled-features/use-refresh-enabled-features';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { useFieldArray, useForm } from 'react-hook-form';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { shouldShowCadSection } from './helpers';
import { useEsriMapStore } from '../map-window/hooks/use-esri-map-store';
import { useEsriMapBootstrap } from '../map-window/hooks/use-esri-map-bootstrap';
import {
    CAD_SECTIONS_DIVIDER_MARGIN,
    ENABLE_UNIT_CLUSTERING_ON_MAP,
    CAD_EVENTS_WINDOW_PREFERENCES,
    CAD_MAP_WINDOW_PREFERENCES,
    CAD_QUEUES_WINDOW_PREFERENCES,
    SEARCH_WINDOWS_PREFERENCES,
    FR_MAP_SECTION_PREFERENCES,
    DEFAULT_NARRATIVE_DIRECTION_NEW_ON_TOP,
} from './constants';
import { useApplicationType } from '../common/hooks/use-application-type';
import { PreferencesFormType } from './types';
import { PreferencesModalSection } from './preferences-modal-section';
import { SetDefaultMapViewPreference } from './set-default-map-view-preference';
import { ThemePreferences } from './theme-preferences';
import { NarrativeDirectionPreference } from './narrative-direction-preference';
import { updateNarrativeDirectionPendoProperty } from '../monitoring/user-action-tracker/pendo';
import { useThemeStore } from '~/theme/use-theme-store';
import { useEsriMapViewRef } from '../map-window/hooks/use-esri-map-view-ref';
import { useWorkspaceStore } from '../panels/use-workspace-store';
import { isValidLatLng } from '../location/helpers';
import { GeoPoint } from '../location/types';
import { ESRI_MAP_DEFAULT_ZOOM_LEVEL } from '../esri/configurations';
import { MAP_WINDOW_PATH } from '../router/constants';
import { useBroadcast } from '~/common/broadcast-channel/use-broadcast';

export type PreferencesModalType = typeof PreferencesModal;
export function PreferencesModal(): React.ReactElement {
    const { isFirstResponder } = useApplicationType();
    const location = useLocation();
    const modalControls = useFullscreenModalControls();
    const refreshEnabledFeatures = useRefreshEnabledFeatures();
    const {
        enableUnitClusteringOnMap,
        rollOutPersistWorkspaceMapFiltersEnabled,
        rollOutWorkspaceMapCenterpointEnabled,
        allowLightModeEnabled,
        rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
        defaultNarrativeDirectionNewOnTop,
    } = useEnabledFeatures();
    const apiClient = useApiClient();
    const { isDarkMode, isLightMode, inactiveTheme } = useThemeStore();
    const mapStore = useEsriMapStore();
    const esriMapViewRef = useEsriMapViewRef();
    const workspaceStore = useWorkspaceStore();
    const mapBootstrapQuery = useEsriMapBootstrap(mapStore);
    const showSetDefaultMapView =
        rollOutPersistWorkspaceMapFiltersEnabled &&
        rollOutWorkspaceMapCenterpointEnabled &&
        esriMapViewRef.current &&
        location.pathname.includes(MAP_WINDOW_PATH);

    const form = useForm<PreferencesFormType>({
        defaultValues: {
            preferences: [],
            mapCenter: {},
            mapZoom: null,
        },
        mode: 'onBlur',
    });

    const query = useQuery({
        queryKey: ['preferences-modal'],
        queryFn() {
            return apiClient.request<PreferencesResource.GetUserPreferences>({
                method: 'GET',
                url: 'preferences/user/',
            });
        },
    });

    const { postMessage } = useBroadcast('preferences-notifications');

    const refreshThemeIfNecessary = () => {
        const preferenceIsLightTheme = form
            .getValues('preferences')
            .find(
                (pref) =>
                    pref.preference ===
                    (isFirstResponder
                        ? 'FIRST_RESPONDER_LIGHT_MODE_ENABLED'
                        : 'CAD_LIGHT_MODE_ENABLED')
            )?.value;

        if ((preferenceIsLightTheme && isDarkMode) || (!preferenceIsLightTheme && isLightMode)) {
            postMessage({
                type: 'THEME_CHANGED',
                payload: { theme: inactiveTheme },
            });
        }
    };

    const refreshMapIfNecessary = () => {
        const curEnableUnitClusteringOnMap = form
            .getValues('preferences')
            .find((pref) => pref.preference === ENABLE_UNIT_CLUSTERING_ON_MAP)?.value;

        if (!!curEnableUnitClusteringOnMap !== enableUnitClusteringOnMap) {
            mapBootstrapQuery.refetch();
        }
    };

    const refreshNarrativePendoIfNecessary = () => {
        const curNarrativeDirectionNewOnTop = form
            .getValues('preferences')
            .find((pref) => pref.preference === DEFAULT_NARRATIVE_DIRECTION_NEW_ON_TOP)?.value;

        if (!!curNarrativeDirectionNewOnTop !== defaultNarrativeDirectionNewOnTop) {
            updateNarrativeDirectionPendoProperty(!!curNarrativeDirectionNewOnTop);
        }
    };

    const saveMapCenterAndZoom = () => {
        const formValues = form.getValues();
        const mapCenter = isValidLatLng(
            formValues?.mapCenter?.latitude,
            formValues?.mapCenter?.longitude
        )
            ? formValues.mapCenter
            : undefined;
        const mapZoom = formValues?.mapZoom;
        if (mapCenter) {
            mapStore.setMapCenter(mapCenter as GeoPoint);
            mapStore.setMapZoomLevel(mapZoom ?? ESRI_MAP_DEFAULT_ZOOM_LEVEL);

            const mapWorkspaceWindow = workspaceStore.getWorkspaceWindowsByWindowType(
                'MAP_V2'
            )?.[0];
            mapWorkspaceWindow?.workspaceMap?.updateMapCenter(
                mapCenter as GeoPoint,
                mapZoom ?? undefined
            );
            workspaceStore.enqueueSaveWorkspaceWindow.flush();
        }
    };

    const mutation = useMutation(
        (args: PreferencesFormType) => {
            return apiClient.request<PreferencesResource.UpsertUserPreferences>({
                method: 'POST',
                url: 'preferences/user/',
                data: {
                    preferenceOptions: args.preferences ?? [],
                },
            });
        },
        {
            onSuccess() {
                saveMapCenterAndZoom();
                refreshThemeIfNecessary();
                refreshMapIfNecessary();
                refreshNarrativePendoIfNecessary();
                refreshEnabledFeatures();
                modalControls.close();
            },
            onError() {
                saveMapCenterAndZoom();
            },
        }
    );

    const setDefaultMapView = useCallback(() => {
        const currentCenter = esriMapViewRef.current?.center;
        const { latitude, longitude } = currentCenter ?? {};
        const zoomLevel = esriMapViewRef.current?.zoom ?? ESRI_MAP_DEFAULT_ZOOM_LEVEL;
        const centerToSet = isValidLatLng(latitude, longitude)
            ? ({ latitude, longitude } as GeoPoint)
            : undefined;

        if (centerToSet) {
            form.setValue('mapCenter', centerToSet);
            form.setValue('mapZoom', zoomLevel);
        }
    }, [esriMapViewRef, form]);

    useEffect(() => {
        form.reset({
            preferences: query.data?.data.data?.preferenceOptions ?? [],
            mapCenter: {},
            mapZoom: null,
        });
    }, [query.data, form]);

    const { fields, update } = useFieldArray({
        control: form.control,
        name: 'preferences',
    });

    const getFormPreference = useCallback(
        (preference: PreferenceEnumType) => {
            return form.getValues('preferences').find((pref) => pref.preference === preference);
        },
        [form]
    );

    const getFormPreferenceValueWithIndex = useCallback(
        (preference: PreferenceEnumType) => {
            for (let index = 0; index < fields.length; index++) {
                if (fields[index].preference === preference) {
                    const currentValue = form.getValues(`preferences.${index}.value`);
                    return [index, currentValue] as const;
                }
            }
            return [-1, false] as const;
        },
        [fields, form]
    );

    const isFieldEnabled = useCallback(
        (preference: PreferenceEnumType) => {
            switch (preference) {
                case PreferenceEnum.CONFIRM_UNIT_ASSIGNMENT_VIA_DRAG_AND_DROP.name:
                    return !!getFormPreference(PreferenceEnum.USER_DRAG_AND_DROP_DISPATCH.name)
                        ?.value;
                default:
                    return true;
            }
        },
        [getFormPreference]
    );

    const onClickUserDragAndDropDispatch = useCallback(() => {
        const userDragAndDropDispatchValue = getFormPreference(
            PreferenceEnum.USER_DRAG_AND_DROP_DISPATCH.name
        )?.value;

        const [
            confirmUnitAssignmentViaDragAndDropIndex,
            confirmUnitAssignmentViaDragAndDropValue,
        ] = getFormPreferenceValueWithIndex(
            PreferenceEnum.CONFIRM_UNIT_ASSIGNMENT_VIA_DRAG_AND_DROP.name
        );

        const isConfirmEnabled = !userDragAndDropDispatchValue
            ? false
            : confirmUnitAssignmentViaDragAndDropValue;

        form.setValue(
            `preferences.${confirmUnitAssignmentViaDragAndDropIndex}.value`,
            isConfirmEnabled
        );

        const confirmUnitAssignmentViaDragAndDrop = getFormPreference(
            PreferenceEnum.CONFIRM_UNIT_ASSIGNMENT_VIA_DRAG_AND_DROP.name
        );

        if (confirmUnitAssignmentViaDragAndDrop) {
            update(confirmUnitAssignmentViaDragAndDropIndex, {
                ...confirmUnitAssignmentViaDragAndDrop,
                value: isConfirmEnabled,
            });
        }
    }, [form, getFormPreference, getFormPreferenceValueWithIndex, update]);

    const onClickPreference = useCallback(
        (preference: PreferenceEnumType) => {
            if (preference === PreferenceEnum.USER_DRAG_AND_DROP_DISPATCH.name) {
                onClickUserDragAndDropDispatch();
            }
        },
        [onClickUserDragAndDropDispatch]
    );

    const userActionTracker = useUserActionTracker();

    const showCadQueuesWindowSection = useMemo(
        () => shouldShowCadSection(fields, CAD_QUEUES_WINDOW_PREFERENCES, isFirstResponder),
        [fields, isFirstResponder]
    );

    const showCadEventsWindowSection = useMemo(
        () => shouldShowCadSection(fields, CAD_EVENTS_WINDOW_PREFERENCES, isFirstResponder),
        [fields, isFirstResponder]
    );

    const showCadMapWindowSection = useMemo(
        () =>
            showSetDefaultMapView ||
            shouldShowCadSection(fields, CAD_MAP_WINDOW_PREFERENCES, isFirstResponder),
        [fields, isFirstResponder, showSetDefaultMapView]
    );

    const showSearchWindowsSection = useMemo(
        () => fields.some((field) => SEARCH_WINDOWS_PREFERENCES.includes(field.preference)),
        [fields]
    );

    return (
        <BlankModal size="md" isOpen={true} onClose={() => modalControls.close()}>
            <ModalHeader>Preferences</ModalHeader>
            <IntegratedFormProvider form={form} query={query} mutation={mutation}>
                <>
                    <ModalBody>
                        <FormBody>
                            <>
                                {isFirstResponder ? (
                                    <>
                                        <PreferencesModalSection
                                            title="Map"
                                            fields={fields}
                                            preferencesToDisplay={FR_MAP_SECTION_PREFERENCES}
                                            isFieldEnabled={isFieldEnabled}
                                            onClickPreference={onClickPreference}
                                            testId="fr-window-preferences"
                                        />
                                        {showSetDefaultMapView && (
                                            <SetDefaultMapViewPreference
                                                setDefaultMapView={setDefaultMapView}
                                            />
                                        )}
                                        {rollOutCadNarrativeFilteringAndFocusImprovementsEnabled && (
                                            <>
                                                <Divider
                                                    style={{ margin: CAD_SECTIONS_DIVIDER_MARGIN }}
                                                />
                                                <NarrativeDirectionPreference
                                                    fields={fields}
                                                    sectionTitle="Events"
                                                />
                                            </>
                                        )}
                                        {showSearchWindowsSection && (
                                            <>
                                                <Divider
                                                    style={{ margin: CAD_SECTIONS_DIVIDER_MARGIN }}
                                                />
                                                <PreferencesModalSection
                                                    title="Search Windows"
                                                    fields={fields}
                                                    preferencesToDisplay={
                                                        SEARCH_WINDOWS_PREFERENCES
                                                    }
                                                    isFieldEnabled={isFieldEnabled}
                                                    onClickPreference={onClickPreference}
                                                    testId="fr-search-windows-preferences"
                                                />
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {showCadQueuesWindowSection && (
                                            <PreferencesModalSection
                                                title="Queues Window"
                                                fields={fields}
                                                preferencesToDisplay={CAD_QUEUES_WINDOW_PREFERENCES}
                                                isFieldEnabled={isFieldEnabled}
                                                onClickPreference={onClickPreference}
                                                testId="cad-queues-window-preferences"
                                            />
                                        )}
                                        {showCadQueuesWindowSection &&
                                            showCadEventsWindowSection && (
                                                <Divider
                                                    style={{ margin: CAD_SECTIONS_DIVIDER_MARGIN }}
                                                />
                                            )}
                                        {showCadEventsWindowSection && (
                                            <PreferencesModalSection
                                                title="Events Window"
                                                fields={fields}
                                                preferencesToDisplay={CAD_EVENTS_WINDOW_PREFERENCES}
                                                isFieldEnabled={isFieldEnabled}
                                                onClickPreference={onClickPreference}
                                                testId="cad-events-window-preferences"
                                            />
                                        )}
                                        {rollOutCadNarrativeFilteringAndFocusImprovementsEnabled && (
                                            <NarrativeDirectionPreference fields={fields} />
                                        )}
                                        {showCadEventsWindowSection && showCadMapWindowSection && (
                                            <Divider
                                                style={{ margin: CAD_SECTIONS_DIVIDER_MARGIN }}
                                            />
                                        )}
                                        {showCadMapWindowSection && (
                                            <>
                                                <PreferencesModalSection
                                                    title="Map Window"
                                                    fields={fields}
                                                    preferencesToDisplay={
                                                        CAD_MAP_WINDOW_PREFERENCES
                                                    }
                                                    isFieldEnabled={isFieldEnabled}
                                                    onClickPreference={onClickPreference}
                                                    testId="cad-map-window-preferences"
                                                />
                                                {showSetDefaultMapView && (
                                                    <SetDefaultMapViewPreference
                                                        setDefaultMapView={setDefaultMapView}
                                                    />
                                                )}
                                            </>
                                        )}
                                        {showCadMapWindowSection && showSearchWindowsSection && (
                                            <Divider
                                                style={{ margin: CAD_SECTIONS_DIVIDER_MARGIN }}
                                            />
                                        )}
                                        {showSearchWindowsSection && (
                                            <PreferencesModalSection
                                                title="Search Windows"
                                                fields={fields}
                                                preferencesToDisplay={SEARCH_WINDOWS_PREFERENCES}
                                                isFieldEnabled={isFieldEnabled}
                                                onClickPreference={onClickPreference}
                                                testId="cad-search-windows-preferences"
                                            />
                                        )}
                                    </>
                                )}
                                {allowLightModeEnabled && (
                                    <>
                                        <Divider style={{ margin: CAD_SECTIONS_DIVIDER_MARGIN }} />
                                        <ThemePreferences
                                            fields={fields}
                                            isFirstResponder={isFirstResponder}
                                        />
                                    </>
                                )}
                            </>
                        </FormBody>
                    </ModalBody>
                    <ModalFooter>
                        <HStack>
                            <FormCancelButton
                                onClick={(event) => {
                                    userActionTracker?.track(
                                        {
                                            trackType: 'CANCEL_MODAL',
                                            component: 'Modal',
                                            feature: 'Preferences Modal',
                                        },
                                        event
                                    );
                                    modalControls.close();
                                }}
                            />
                            <FormSaveButton>Save</FormSaveButton>
                        </HStack>
                    </ModalFooter>
                </>
            </IntegratedFormProvider>
        </BlankModal>
    );
}
